/**
 * Minimal document-head manager.
 *
 * Why not react-helmet-async: this app is on React 19, and react-helmet-async's
 * published peer range stops at React 18, so adding it would mean shipping an
 * unmaintained dependency past its supported range plus ~6KB to every bundle —
 * for a job that is ~100 lines here. React 19's built-in metadata hoisting was
 * the other candidate, but it APPENDS tags rather than reconciling with the
 * per-URL tags the prerenderer already wrote into <head>, which would leave two
 * <title> and two rel=canonical elements on every prerendered page.
 *
 * So: one imperative manager, used by both sides. The build-time prerenderer
 * writes head tags carrying `data-lb-seo="managed"`, and on hydration this
 * module ADOPTS and replaces exactly those tags. There is never a moment with
 * two canonicals, and a client-side route change fully swaps the head.
 *
 * CommonJS on purpose — shared by the CRA bundle and the Node build scripts.
 */

const { serializeJsonLd } = require('./structuredData');

/** Marks every tag this module (or the prerenderer) owns. */
const MANAGED_ATTRIBUTE = 'data-lb-seo';
const MANAGED_VALUE = 'managed';

/**
 * Flatten a metadata object into the list of head tags it implies.
 *
 * @param {Object} metadata a `PageMetadata` from metadata.js
 * @returns {Array<{ tag: 'meta' | 'link', attrs: Record<string, string> }>}
 */
function metadataToTags(metadata) {
  const m = metadata || {};
  /** @type {Array<{ tag: 'meta' | 'link', attrs: Record<string, string> }>} */
  const tags = [];

  if (m.description) {
    tags.push({ tag: 'meta', attrs: { name: 'description', content: m.description } });
  }
  if (m.robots) {
    tags.push({ tag: 'meta', attrs: { name: 'robots', content: m.robots } });
    // Googlebot honours the generic directive, but an explicit one removes any
    // ambiguity when a page is noindex for eligibility reasons.
    tags.push({ tag: 'meta', attrs: { name: 'googlebot', content: m.robots } });
  }
  if (m.canonical) {
    tags.push({ tag: 'link', attrs: { rel: 'canonical', href: m.canonical } });
  }

  const og = m.openGraph || {};
  const ogPairs = [
    ['og:title', og.title],
    ['og:description', og.description],
    ['og:url', og.url],
    ['og:type', og.type],
    ['og:image', og.image],
    ['og:site_name', og.siteName],
    ['og:locale', 'en_US'],
  ];
  ogPairs.forEach((pair) => {
    if (pair[1]) tags.push({ tag: 'meta', attrs: { property: pair[0], content: String(pair[1]) } });
  });

  const tw = m.twitter || {};
  const twPairs = [
    ['twitter:card', tw.card],
    ['twitter:title', tw.title],
    ['twitter:description', tw.description],
    ['twitter:image', tw.image],
  ];
  twPairs.forEach((pair) => {
    if (pair[1]) tags.push({ tag: 'meta', attrs: { name: pair[0], content: String(pair[1]) } });
  });

  return tags;
}

/**
 * Render the managed head tags as an HTML string. Used by the build-time
 * prerenderer to write real per-URL head tags into the static document.
 *
 * @param {Object} metadata
 * @param {Array<Object>} [jsonLd]
 * @returns {string}
 */
function renderHeadTagsHtml(metadata, jsonLd) {
  const escapeAttr = (value) =>
    String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  const lines = [];
  if (metadata && metadata.title) {
    lines.push(
      `<title ${MANAGED_ATTRIBUTE}="${MANAGED_VALUE}">${escapeAttr(metadata.title)}</title>`
    );
  }

  metadataToTags(metadata).forEach((tag) => {
    const attrs = Object.keys(tag.attrs)
      .map((key) => `${key}="${escapeAttr(tag.attrs[key])}"`)
      .join(' ');
    lines.push(`<${tag.tag} ${attrs} ${MANAGED_ATTRIBUTE}="${MANAGED_VALUE}">`);
  });

  (Array.isArray(jsonLd) ? jsonLd : []).filter(Boolean).forEach((node) => {
    lines.push(
      `<script type="application/ld+json" ${MANAGED_ATTRIBUTE}="${MANAGED_VALUE}">${serializeJsonLd(
        node
      )}</script>`
    );
  });

  return lines.join('\n    ');
}

/**
 * Apply metadata + JSON-LD to a live document, replacing whatever this module
 * (or the prerenderer) put there previously.
 *
 * @param {Document} doc
 * @param {Object} metadata
 * @param {Array<Object>} [jsonLd]
 */
function applyHead(doc, metadata, jsonLd) {
  if (!doc || !doc.head) return;
  const head = doc.head;

  // Clear previously managed tags (ours or the prerenderer's).
  const previous = head.querySelectorAll(`[${MANAGED_ATTRIBUTE}="${MANAGED_VALUE}"]`);
  for (let i = 0; i < previous.length; i += 1) {
    const node = previous[i];
    // The <title> element is reused rather than removed so the tab never blanks.
    if (node.tagName !== 'TITLE' && node.parentNode) node.parentNode.removeChild(node);
  }

  // The CRA template ships a single static description for every route; once a
  // page takes ownership of the head, that stale tag has to go.
  const staleDescriptions = head.querySelectorAll(
    `meta[name="description"]:not([${MANAGED_ATTRIBUTE}])`
  );
  for (let i = 0; i < staleDescriptions.length; i += 1) {
    const node = staleDescriptions[i];
    if (node.parentNode) node.parentNode.removeChild(node);
  }

  if (metadata && metadata.title) doc.title = metadata.title;

  metadataToTags(metadata).forEach((tag) => {
    const element = doc.createElement(tag.tag);
    Object.keys(tag.attrs).forEach((key) => element.setAttribute(key, tag.attrs[key]));
    element.setAttribute(MANAGED_ATTRIBUTE, MANAGED_VALUE);
    head.appendChild(element);
  });

  (Array.isArray(jsonLd) ? jsonLd : []).filter(Boolean).forEach((node) => {
    const script = doc.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute(MANAGED_ATTRIBUTE, MANAGED_VALUE);
    script.textContent = JSON.stringify(node);
    head.appendChild(script);
  });
}

module.exports = {
  MANAGED_ATTRIBUTE,
  MANAGED_VALUE,
  metadataToTags,
  renderHeadTagsHtml,
  applyHead,
};
